import useActiveCursor from '@/hooks/useActiveCursor.js';
import setQuery from '@/lib/setQuery.js';
import { cleanClass } from '@shared/utils/cleanClass.js';
import { useRef, useState, useEffect } from 'react';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import IconV2 from '../v2/IconV2.jsx';
import { useArgSelector } from '@/redux/curriedSelectors/curriedSelectors.js';
import { query } from '@/redux/selectors/querySelectors.js';

const Progress = ({ percentage, style, ...rest }) => {
  const radius = 16;
  const strokeWidth = 3.5;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = percentage ? circumference - (percentage / 100) * circumference : 0;

  return (
    <div className={'comp-tabs-progress'} style={style} {...rest}>
      <svg viewBox="0 0 36 36">
        <circle className="bg" cx="18" cy="18" r={radius} strokeWidth={strokeWidth} />
        <circle className={`progress success`} cx="18" cy="18" r={radius} strokeWidth={strokeWidth} strokeDasharray={circumference} strokeDashoffset={strokeDashoffset || 0} strokeLinecap="round" />
      </svg>
    </div>
  );
};

const PageTabs = ({ items, tabKey = 'tab', defaultTab, onClick, onChange, withStepNumber = false, stepDone = [], style = {}, scrollable = false }) => {
  const tabsRef = useRef(null);
  const containerRef = useRef(null);
  const [showLeftGradient, setShowLeftGradient] = useState(false);
  const [showRightGradient, setShowRightGradient] = useState(false);

  const defaultTabValue = defaultTab || items?.[0]?.value;
  const activeTab = useArgSelector(query, tabKey) || defaultTabValue;

  const itemLabels = items?.map((n) => n.label)?.join(',');
  const itemCounts = items?.map((n) => n.count)?.join(',');
  const itemsHaveActive = items?.some((n) => n.active);
  const mappedItems = items?.map((n) => (itemsHaveActive ? n : { ...n, active: n.value === activeTab }));

  const activeIndex = mappedItems.findIndex((item) => item.active);
  const cursorStyle = useActiveCursor({ activeIndex, containerRef: tabsRef, offset: 0 }, [itemLabels, itemCounts]);

  const showProgress = (item) => item.progress !== undefined && item.progress !== 100 && item.progress !== 0;

  const handleTabClick = (value, disabled = false) => {
    if (disabled) {
      return;
    }
    onChange?.(value);
    if (typeof onClick === 'function') {
      onClick(value);
    }
    setQuery({ [tabKey]: value });
  };

  // Scrollable functionality
  const checkScroll = () => {
    if (!scrollable || !containerRef.current) return;

    const { scrollLeft, scrollWidth, clientWidth } = containerRef.current;

    // Show left gradient if scrolled right from the start
    setShowLeftGradient(scrollLeft > 10);

    // Show right gradient if there's content to scroll to the right
    setShowRightGradient(scrollLeft < scrollWidth - clientWidth - 10);
  };

  useEffect(() => {
    if (!scrollable) return;

    checkScroll();

    // Add resize observer to check scroll on container size changes
    let rafId = null;
    const resizeObserver = new ResizeObserver(() => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(checkScroll);
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      cancelAnimationFrame(rafId);
      resizeObserver.disconnect();
    };
  }, [items, scrollable]);

  const handleScroll = (direction) => {
    if (!containerRef.current) return;

    const scrollAmount = 200; // Scroll by 200px
    const newScrollLeft = direction === 'left' ? containerRef.current.scrollLeft - scrollAmount : containerRef.current.scrollLeft + scrollAmount;

    containerRef.current.scrollTo({
      left: newScrollLeft,
      behavior: 'smooth'
    });
  };

  const handleWheel = (e) => {
    if (!scrollable) return;

    // Allow horizontal scrolling with shift + wheel or trackpad horizontal scroll
    if (e.shiftKey || Math.abs(e.deltaX) > 0) {
      e.preventDefault();
      containerRef.current.scrollLeft += e.shiftKey ? e.deltaY : e.deltaX;
      checkScroll();
    }
  };

  const tabsContent = (
    <div className="comp-page-tabs-container" style={style}>
      <div ref={tabsRef} className="comp-page-tabs">
        {mappedItems.map((item, idx) =>
          item.hidden ? null : (
            <div key={item.label} className={cleanClass(`comp-page-tab ${item.active ? 'active' : ''} ${item?.className || ''}`)} onClick={() => handleTabClick(item.value, item.disabled)} data-tab={item.value} data-title={item.title}>
              {withStepNumber && <div className={cleanClass(`comp-page-tab-number ${stepDone[idx] ? 'done' : ''}`)}>{idx + 1}</div>}
              <span>{item.label}</span>
              {item.icon && <IconV2 icon={item.icon} {...(item?.iconProps || {})} />}
              {showProgress(item) && <Progress percentage={item.progress} />}
              {item.count !== undefined && (
                <div className={'count'}>
                  {item.count}
                  {item?.filtered && <span className={'filtered'}>&nbsp;with Search</span>}
                </div>
              )}
            </div>
          )
        )}
        <div className="comp-page-tabs-active-cursor" style={cursorStyle} />
      </div>
    </div>
  );

  if (!scrollable) {
    return tabsContent;
  }

  return (
    <div className="comp-page-scrollable-tabs-wrapper">
      {showLeftGradient && (
        <div className="comp-page-scrollable-tabs-gradient left">
          <button className="comp-page-scrollable-tabs-arrow" onClick={() => handleScroll('left')} aria-label="Scroll left">
            <IconV2 icon={FiChevronLeft} />
          </button>
        </div>
      )}

      <div ref={containerRef} className="comp-page-scrollable-tabs-container" onScroll={checkScroll} onWheel={handleWheel}>
        {tabsContent}
      </div>

      {showRightGradient && (
        <div className="comp-page-scrollable-tabs-gradient right">
          <button className="comp-page-scrollable-tabs-arrow" onClick={() => handleScroll('right')} aria-label="Scroll right">
            <IconV2 icon={FiChevronRight} />
          </button>
        </div>
      )}
    </div>
  );
};

export default PageTabs;
